import UsersApiV2 from 'api/v2/admins/users-api-v2'
import React, { useState, useEffect, useRef } from 'react'
import {
  Badge,
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  FormFeedback,
  FormGroup,
  Label,
  Input,
  Spinner
} from 'reactstrap'
import PropTypes from 'prop-types'
import { useField } from 'formik'

const propTypes = {
  formGroupClassName : PropTypes.string,
  label              : PropTypes.string,
  required           : PropTypes.bool,
  name               : PropTypes.string,
  id                 : PropTypes.number
}

const defaultProps = {
  formGroupClassName : '',
  label              : 'Referrer',
  required           : false
}

const DEFAULT_OPTION = {
  tinkerlust : 'tinkerlust',
  cs         : 'cs',
  psa        : 'psa'
}

/**
 * @param {number} formGroupClassName - Field Label
 * @param {string} label - Field Label
 * @param {boolean} required - Field Label
 */
const ReferrerInputField = ({
  formGroupClassName,
  label,
  required,
  ...props
}) => {
  const [ field, meta, helper ] = useField(props)

  const valid           = meta.touched && !meta.error
  const invalid         = meta.touched && !!meta.error
  const validationStyle = valid
    ? 'is-valid'
    : (invalid ? 'is-invalid' : '')

  const [ referrers, setReferrers ]       = useState([])
  const [ isLoading, setIsLoading ]       = useState(false)
  const [ dropdownOpen, setDropdownOpen ] = useState(false)

  const inputRef = useRef()

  const toggle = () => setDropdownOpen((prevState) => !prevState)

  // Fetch List All Referrers from backend once
  useEffect(() => {
    setIsLoading(true)
    UsersApiV2.referrers()
      .then((response) => {
        const options = referrerOptions(response.data)

        setReferrers(options)
        setIsLoading(false)
      })
      .catch((errors) => {
        alert(errors)
      })
  }, [])

  // Add 'DEFAULT_OPTION' as options
  const referrerOptions = (referrers) => {
    const arrayOtherOption = Object.values(DEFAULT_OPTION)
    const newReferrers     = [ ...referrers, ...arrayOtherOption ]

    // Remove duplicate name
    const distinctReferrers = [ ...new Set(newReferrers) ]
    return distinctReferrers
  }

  const handleClickOption = (option) => {
    setDropdownOpen((state) => !state)
    helper.setValue(option)
  }

  const handleClickAddNewOption = () => {
    const freeTextOptionValue = inputRef.current.value
    setDropdownOpen((state) => !state)
    helper.setValue(freeTextOptionValue)
  }

  return (
    <FormGroup className={ formGroupClassName }>
      {
        label && (
          <Label
            for={ props.id || props.name }
            className='text-gray-light text-capitalize'
          >
            <strong>{ label }</strong>
          </Label>
        )
      }
      {
        required && (
          <Badge
            color='warning'
            className='ml-1'
          >
            <i className='fas fa-asterisk' />
          </Badge>
        )
      }

      <Dropdown
        isOpen={ dropdownOpen }
        toggle={ toggle }
        className={ 'd-block ' }
      >
        <DropdownToggle
          tag={ 'div' }
          className={ `form-control d-flex align-items-center ${validationStyle}` }
        >
          { field.value || 'Choose Referrer' }
        </DropdownToggle>

        <DropdownMenu className='w-100'>
          {
            isLoading && (
              <DropdownItem>
                <Spinner size={ 'sm' } />
              </DropdownItem>
            )
          }
          {
            referrers.map((option, index) => (
              <DropdownItem
                className='text-wrap'
                key={ `select-multi-option-${option}-${index}` }
                onClick={ () => handleClickOption(option) }
                toggle={ false }
              >
                { option }
              </DropdownItem>
            ))
          }
          {
            <DropdownItem toggle={ false } tag='div'>
              <span>Other:</span>
              <Input innerRef={ inputRef } />
              <Button
                className='mt-1'
                type={ 'button' }
                onClick={ handleClickAddNewOption }
                color='primary'
              >{ 'Add' }</Button>
            </DropdownItem>
          }
        </DropdownMenu>
      </Dropdown>

      { meta.error && <FormFeedback>{ meta.error }</FormFeedback> }
    </FormGroup>
  )
}

ReferrerInputField.propTypes    = propTypes
ReferrerInputField.defaultProps = defaultProps

export default ReferrerInputField
